import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, of, switchMap, tap } from 'rxjs';
import { ConfigurationManagerService } from './configuration-manager.service';
import { jwtDecode } from 'jwt-decode';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root'
})
export class PapiAuthService {
  private user: any = null;
  private initialized = false;
  private staySignedIn = false;
  private refreshTimer: any;

  constructor(
    private api: ApiService,
    private http: HttpClient,
  ) {
  }

  // Methods
  login(username: string, password: string): Observable<any> {
    const parameters = {
      extraHttpRequestParams: {
        headers: new HttpHeaders({
          Authorization: `Basic ${btoa(username + ':' + password)}`
        })
      }
    };
    return this.http.post('your-api-endpoint', parameters).pipe(
      tap((response: any) => {
        if (response.token) {
          // Set user synchronously if possible
          this.user = { username, token: response.token, expiresIn: response.expiresIn };
          this.setCredentials(username, response.token, response.expiresIn);
        }
      }),
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }
  
  isLoggedIn(): boolean {
    const user = sessionStorage.getItem('User');
    if (user) {
      this.user = JSON.parse(user);
    }
    return !!(this.user && this.user.token);
  }

  loginWithToken(tokenData: any): Observable<any> {
    let token = tokenData.id_token;
    return this.setCredentials('', token, tokenData.expiresIn);
  }

  schedulePapiRefreshToken(token: string): void {
    if (!token) {
      return;
    }

    const secRemaining = this.secUntilExpire(token);
    const secEarly = 15;
    let secNext = secRemaining - secEarly;
    secNext = secNext <= 0 ? 0 : secNext;

    if (secRemaining <= 0) {
      return;
    }

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    this.refreshTimer = setTimeout(() => {
      this.getPapiRefreshToken().subscribe((user) => {
        if (!user) return;
        sessionStorage.setItem('User', JSON.stringify(user));
        this.schedulePapiRefreshToken(user.token);
      });
    }, secNext * 1000);
  }

  private secUntilExpire(token: string): number {
    try {
      const decoded: any = jwtDecode(token);
      const expiresInMs = this.parseExpiresInToMilliseconds(decoded.expiresIn);
      const expAt = decoded.iat + (expiresInMs / 1000);
      const now = Math.floor(Date.now() / 1000);
      return expAt - now;
    } catch (error) {
      return -1;
    }
  }

  private parseExpiresInToMilliseconds(expiresIn: string | number): number {
    if (typeof expiresIn === 'string') {
      return this.parseExpiresIn(expiresIn);
    } else {
      return expiresIn * 1000;
    }
  }

  private parseExpiresIn(val: string): number {
    const s = 1000, m = s * 60, h = m * 60, d = h * 24, y = d * 365.25;
    const match = /^((?:\d+)?\.?\d+) *(milliseconds?|msecs?|ms|seconds?|secs?|s|minutes?|mins?|m|hours?|hrs?|h|days?|d|years?|yrs?|y)?$/i.exec(val);
    if (!match) return 0;
    const n = parseFloat(match[1]);
    const type = (match[2] || 'ms').toLowerCase();
    switch (type) {
      case 'years': case 'year': case 'yrs': case 'yr': case 'y': return n * y;
      case 'days': case 'day': case 'd': return n * d;
      case 'hours': case 'hour': case 'hrs': case 'hr': case 'h': return n * h;
      case 'minutes': case 'minute': case 'mins': case 'min': case 'm': return n * m;
      case 'seconds': case 'second': case 'secs': case 'sec': case 's': return n * s;
      case 'milliseconds': case 'millisecond': case 'msecs': case 'msec': case 'ms': return n;
      default: return 0;
    }
  }

  getPapiRefreshToken(): Observable<any> {
    const url = ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.RefreshToken;
    return this.api.get(url).pipe(
      tap((response: any) => {
        if (response.token) {
          this.setCredentials('', response.token, response.expiresIn);
        }
      }),
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  private isTokenExpired(token: string): boolean {
    try {
      // const decoded: any = jwt_decode(token);
      // const now = moment();
      // return now.isBefore(decoded.exp);
      return false;
    } catch (error) {
      return true;
    }
  }


  // Set user credentials
  private setCredentials(username: string, token: string, expiresIn: number): Observable<any> {
    localStorage.setItem('PAA_AUTH_TOKEN', JSON.stringify(token));
    this.setHeaders(token);
    return this.buildUser(username, token, expiresIn);
  }

  // Set headers for API calls
  private setHeaders(token: string): void {
    // Set default headers or use a library to manage headers
    // Example: this.httpClient.defaults.headers = { Authorization: token };
  }

  // Build user from session data
  private buildUser(username: string, token: string, expiresIn: number): Observable<any> {
    const tasks = {
      user: this.getSessionUser(),
    };

    return of(tasks).pipe(
      switchMap(() => {
        this.setUser({ ...tasks.user, token, expiresIn });
        return this.setProfile();
      }),
      catchError((error) => {
        console.error(error);
        this.clearCredentials();
        return of(null);
      })
    );
  }

  // Set user information
  private setUser(user: any): void {
    this.user = user;
  }

  // Set profile
  private setProfile(): Observable<any> {
    var user = JSON.parse(sessionStorage.getItem('User')!);
    const param = { id: user.id };
    return this.http.post(ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.Profile, param).pipe(
      tap((result: any) => {
        this.user.profile = { ...this.user.profile, ...result.data };
        return this.user;
      }),
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  // Get session user data
  private getSessionUser(): Observable<any> {
    return this.http.get('your-session-user-endpoint').pipe(
      tap((response: any) => response.data)
    );
  }

  // Clear credentials and logout
  clearCredentials(): void {
    sessionStorage.removeItem('User');
    localStorage.removeItem('User');
    localStorage.removeItem('PAA_AUTH_TOKEN');
    localStorage.clear();
    this.setUser({});
  }

  // Logout user
  logout(): void {
    this.clearCredentials();
  }
}